<template>
  <div v-if="isAdmin" class="domain-switcher">
    <button 
      @click="switchDomain" 
      class="flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 rounded-md transition-colors duration-200 border border-gray-300 shadow-sm"
      :title="$t('domain_switcher.tooltip')"
    >
      <!-- Current Domain (Highlighted) -->
      <div class="flex items-center px-2 py-1 bg-primary-100 border border-primary-300 rounded">
        <img :src="currentFlagImage" :alt="currentDomain" class="w-5 h-5 mr-1.5 rounded" />
        <span class="font-bold text-primary-700">{{ currentDomainCode }}</span>
      </div>
      
      <!-- Arrow Icon -->
      <BaseIcon icon="arrow-right" class="mx-2 text-gray-400" />
      
      <!-- Target Domain -->
      <div class="flex items-center opacity-70">
        <img :src="targetFlagImage" :alt="targetDomain" class="w-5 h-5 mr-1.5 rounded" />
        <span class="font-semibold text-gray-600">{{ targetDomainCode }}</span>
      </div>
    </button>
  </div>
</template>

<script>
import { getSiteContext, isNorwegianSite } from '@/utils/siteContext';

export default {
  name: 'DomainSwitcher',
  
  data() {
    return {
      isAdmin: false,
    };
  },
  
  computed: {
    currentDomain() {
      return getSiteContext();
    },
    
    currentFlagImage() {
      return require(`@/assets/images/flags/${this.currentDomain}.svg`);
    },
    
    targetDomain() {
      return this.currentDomain === 'no' ? 'se' : 'no';
    },
    
    targetFlagImage() {
      return require(`@/assets/images/flags/${this.targetDomain}.svg`);
    },
    
    currentDomainCode() {
      return this.currentDomain.toUpperCase();
    },
    
    targetDomainCode() {
      return this.targetDomain.toUpperCase();
    },
  },
  
  methods: {
    switchDomain() {
      const currentUrl = window.location.href;
      let newUrl;
      
      // Get current hostname
      const hostname = window.location.hostname.toLowerCase();
      console.log('Current hostname: ', hostname);
      
      // Determine target URL based on current domain
      if (isNorwegianSite()) {
        // Switch from Norwegian to Swedish
        if (hostname === 'localhost.no') {
          // localhost: localhost.no → localhost.se
          newUrl = currentUrl.replace('localhost.no', 'localhost.se');
        } else if (hostname === 'securcloudlocal.no') {
          // localhost: securcloudlocal.no → securcloudlocal.se
          newUrl = currentUrl.replace('securcloudlocal.no', 'securcloudlocal.se');
        } else if (hostname === 'securcloud.securdia.no') {
          // production: securcloud.securdia.no → securcloud.securdia.se
          newUrl = currentUrl.replace('securcloud.securdia.no', 'securcloud.securdia.se');
        } else if (hostname === 'staging.securdia.no') {
          // staging: staging.securdia.no → staging.securdia.se
          newUrl = currentUrl.replace('staging.securdia.no', 'staging.securdia.se');
        }
      } else {
        // Switch from Swedish to Norwegian
        if (hostname === 'localhost.se') {
          // localhost: localhost.se → localhost.no
          newUrl = currentUrl.replace('localhost.se', 'localhost.no');
        } else if (hostname === 'securcloudlocal.se') {
          // localhost: securcloudlocal.se → securcloudlocal.no
          newUrl = currentUrl.replace('securcloudlocal.se', 'securcloudlocal.no');
        } else if (hostname === 'securcloud.securdia.se') {
          // production: securcloud.securdia.se → securcloud.securdia.no
          newUrl = currentUrl.replace('securcloud.securdia.se', 'securcloud.securdia.no');
        } else if (hostname === 'staging.securdia.se') {
          // staging: staging.securdia.se → staging.securdia.no
          newUrl = currentUrl.replace('staging.securdia.se', 'staging.securdia.no');
        }
      }
      
      if (newUrl && newUrl !== currentUrl) {
        // Parse URL to handle query parameters properly
        const url = new URL(newUrl);

        // Check if current route has parameters (implies specific resource IDs)
        // If so, redirect to root to avoid 404s or invalid data access on the new domain
        if (this.$route && Object.keys(this.$route.params).length > 0) {
          url.pathname = '/';
          url.search = ''; // Clear existing query params
        }
        
        // Remove any existing domain switch parameters to prevent duplication
        url.searchParams.delete('domain_switched');
        url.searchParams.delete('from');
        
        // Add fresh query parameters to show success message after switch
        url.searchParams.set('domain_switched', 'true');
        url.searchParams.set('from', this.currentDomain);
        
        // Navigate to new domain
        window.location.href = url.toString();
      } else {
        console.error('Could not determine target domain URL: ', newUrl);
        this.$toast.error(this.$t('domain_switcher.error'));
      }
    },
    
    checkAdminRole() {
      const user = this.$store.state.user;
      this.isAdmin = user && (user.role === 'admin' || user.role === 'seller');
    },
  },
  
  created() {
    this.checkAdminRole();
  },
  
  watch: {
    '$store.state.user': {
      handler() {
        this.checkAdminRole();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.domain-switcher button {
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .domain-switcher button {
    padding: 0.5rem;
  }
}
</style>
